/**
 * Resolves which location a free-text instruction refers to.
 *
 * Matching is done against the names in the bulk-edit export rather than the
 * page's location list, so the assistant can only ever target a location the
 * server considers editable.
 *
 * This is deliberately strict. Targeting the wrong location would edit a live
 * listing the user never mentioned, so anything short of a confident single
 * match asks the user instead of guessing.
 */

export interface LocationCandidate {
  id: string;
  name: string;
}

export type LocationMatch =
  | { status: 'RESOLVED'; location: LocationCandidate }
  | { status: 'NONE' }
  | { status: 'AMBIGUOUS'; candidates: LocationCandidate[] };

/** Lowercases and reduces punctuation/whitespace to single spaces. */
export const normalizeName = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** True when `needle` appears in `haystack` on whole-word boundaries. */
const containsPhrase = (haystack: string, needle: string): boolean => {
  if (!needle) return false;
  const index = haystack.indexOf(needle);
  if (index === -1) return false;

  const before = index === 0 ? ' ' : haystack[index - 1];
  const afterIndex = index + needle.length;
  const after = afterIndex >= haystack.length ? ' ' : haystack[afterIndex];
  return before === ' ' && after === ' ';
};

/**
 * Finds the location named in `message`.
 *
 * Longer names win over shorter ones so "Cheyenne North" is not resolved to
 * "Cheyenne"; two different locations matching at the same length is reported
 * as ambiguous rather than resolved arbitrarily.
 */
export const resolveLocationFromMessage = (
  message: string,
  candidates: LocationCandidate[],
): LocationMatch => {
  const haystack = ` ${normalizeName(message)} `;

  const matches = candidates
    .map((candidate) => ({ candidate, normalized: normalizeName(candidate.name) }))
    .filter(({ normalized }) => containsPhrase(haystack, normalized));

  if (matches.length === 0) return { status: 'NONE' };

  const longest = Math.max(...matches.map(({ normalized }) => normalized.length));
  const best = matches.filter(({ normalized }) => normalized.length === longest);

  // Identically-named locations are genuinely ambiguous from the text alone.
  const uniqueIds = new Set(best.map(({ candidate }) => candidate.id));
  if (uniqueIds.size > 1) {
    return { status: 'AMBIGUOUS', candidates: best.map(({ candidate }) => candidate) };
  }

  return { status: 'RESOLVED', location: best[0].candidate };
};
